import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import StripeCheckoutModal from '../components/StripeCheckout';
import {
  Crown,
  Star,
  Zap,
  Check,
  X,
  Sparkles,
  Rocket,
  Shield,
  Users,
  TreeDeciduous,
  Palette,
  PenTool,
  Gamepad2,
  Award,
  Infinity,
  ArrowRight,
  Loader2
} from 'lucide-react';

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  
  // Stripe checkout state
  const [checkoutModal, setCheckoutModal] = useState({
    isOpen: false,
    clientSecret: null,
    tierName: '',
    amount: 0
  });

  useEffect(() => {
    loadPricingData();
  }, [user]);

  const loadPricingData = async () => {
    try {
      setLoading(true);
      const [tiersRes, compareRes] = await Promise.all([
        api.get('/api/subscriptions/tiers'),
        api.get('/api/subscriptions/compare')
      ]);
      
      setTiers(tiersRes.data.tiers || []);
      setComparison(compareRes.data);

      if (user?.id) {
        const subRes = await api.get('/api/subscriptions/me', {
          params: { user_id: user.id }
        });
        setCurrentSubscription(subRes.data.subscription);
      }
    } catch (error) {
      console.error('Error loading pricing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierKey) => {
    if (!user) {
      navigate('/login?redirect=/pricing');
      return;
    }

    // Find the tier to get pricing info
    const tier = tiers.find(t => t.tier_key === tierKey);
    if (!tier) return;

    // Free tier doesn't need Stripe
    if (tier.price_monthly === 0) {
      try {
        setSubscribing(tierKey);
        await api.post('/api/subscriptions/subscribe', {
          tier_key: tierKey,
          billing_period: billingPeriod
        });
        await loadPricingData();
      } catch (error) {
        console.error('Error subscribing:', error);
        alert(error.response?.data?.detail || 'Failed to subscribe');
      } finally {
        setSubscribing(null);
      }
      return;
    }

    // Paid tiers use Stripe checkout
    try {
      setSubscribing(tierKey);
      const response = await api.createStripeCheckout(tierKey, billingPeriod);
      
      const amount = billingPeriod === 'yearly'
        ? Math.round(tier.price_monthly * 12 * 0.8 * 100)
        : tier.price_monthly * 100;

      setCheckoutModal({
        isOpen: true,
        clientSecret: response.client_secret,
        tierName: tier.name,
        amount: amount
      });
    } catch (error) {
      console.error('Error subscribing:', error);
      alert(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribing(null);
    }
  };

  const handleCheckoutSuccess = async (paymentIntent) => {
    try {
      // Confirm the payment on our backend
      await api.confirmStripePayment(paymentIntent.id);
      // Reload subscription data
      await loadPricingData();
      setCheckoutModal({ isOpen: false, clientSecret: null, tierName: '', amount: 0 });
    } catch (error) {
      console.error('Error confirming payment:', error);
    }
  };

  const handleCheckoutClose = () => {
    setCheckoutModal({ isOpen: false, clientSecret: null, tierName: '', amount: 0 });
  };

  const getTierIcon = (tierKey) => {
    switch (tierKey) {
      case 'free': return <Star className="w-8 h-8" />;
      case 'premium': return <Crown className="w-8 h-8" />;
      case 'pro': return <Rocket className="w-8 h-8" />;
      default: return <Zap className="w-8 h-8" />;
    }
  };

  const getTierColors = (tierKey) => {
    switch (tierKey) {
      case 'free': 
        return {
          bg: 'bg-gray-800',
          border: 'border-gray-700',
          accent: 'text-gray-400',
          button: 'bg-gray-700 hover:bg-gray-600',
          iconBg: 'bg-gray-700'
        };
      case 'premium':
        return {
          bg: 'bg-gradient-to-br from-purple-900/50 to-indigo-900/50',
          border: 'border-purple-500/50',
          accent: 'text-purple-400',
          button: 'bg-purple-600 hover:bg-purple-500',
          iconBg: 'bg-purple-500/20'
        };
      case 'pro':
        return {
          bg: 'bg-gradient-to-br from-amber-900/50 to-orange-900/50',
          border: 'border-amber-500/50',
          accent: 'text-amber-400',
          button: 'bg-amber-600 hover:bg-amber-500',
          iconBg: 'bg-amber-500/20'
        };
      default:
        return {
          bg: 'bg-gray-800',
          border: 'border-gray-700',
          accent: 'text-gray-400',
          button: 'bg-gray-700 hover:bg-gray-600',
          iconBg: 'bg-gray-700'
        };
    }
  };

  const featureIcons = {
    skill_trees: TreeDeciduous,
    art_studio: Palette,
    writers_studio: PenTool,
    games: Gamepad2,
    achievements: Award,
    community: Users,
    moderation: Shield
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="py-16 px-4 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-amber-400">Creative Journey</span>
        </h1>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
          Unlock powerful tools for writing, art, gaming, and peer review. Level up your skills and earn rewards.
        </p>

        {/* Billing Toggle */}
        <div className="inline-flex items-center gap-4 bg-gray-800 rounded-full p-1">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`px-6 py-2 rounded-full transition-all ${
              billingPeriod === 'monthly'
                ? 'bg-purple-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('yearly')}
            className={`px-6 py-2 rounded-full transition-all flex items-center gap-2 ${
              billingPeriod === 'yearly'
                ? 'bg-purple-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Yearly
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
              Save 20%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const colors = getTierColors(tier.tier_key);
            const isCurrentTier = currentSubscription?.tier_key === tier.tier_key;
            const price = billingPeriod === 'yearly' 
              ? (tier.price_monthly * 12 * 0.8).toFixed(0)
              : tier.price_monthly;
            
            return (
              <div
                key={tier.tier_key}
                className={`relative rounded-2xl p-6 ${colors.bg} border-2 ${colors.border} ${
                  tier.tier_key === 'premium' ? 'md:-mt-4 md:mb-4 scale-105' : ''
                }`}
              >
                {tier.tier_key === 'premium' && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-sm font-semibold px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                )}

                {isCurrentTier && (
                  <div className="absolute -top-3 right-4 bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Current Plan
                  </div>
                )}

                {/* Tier Header */}
                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${colors.iconBg} ${colors.accent} mb-4`}>
                    {getTierIcon(tier.tier_key)}
                  </div>
                  <h3 className="text-2xl font-bold capitalize mb-2">{tier.name}</h3>
                  <p className="text-gray-400 text-sm">{tier.description}</p>
                </div>

                {/* Price */}
                <div className="text-center mb-6">
                  {tier.price_monthly === 0 ? (
                    <div className="text-4xl font-bold">Free</div>
                  ) : (
                    <div>
                      <span className="text-4xl font-bold">${price}</span>
                      <span className="text-gray-400">/{billingPeriod === 'yearly' ? 'year' : 'month'}</span>
                    </div>
                  )}
                </div>

                {/* Skill Trees Limit */}
                <div className={`flex items-center justify-center gap-2 ${colors.accent} font-semibold mb-6 p-3 rounded-xl ${colors.iconBg}`}>
                  <TreeDeciduous className="w-5 h-5" />
                  {tier.max_skill_trees === -1 ? (
                    <span className="flex items-center gap-1">
                      <Infinity className="w-5 h-5" /> Unlimited Skill Trees
                    </span>
                  ) : (
                    <span>{tier.max_skill_trees} Skill Tree{tier.max_skill_trees !== 1 ? 's' : ''}</span>
                  )}
                </div>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  {(tier.features || []).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${colors.iconBg}`}>
                        <Check className={`w-3 h-3 ${colors.accent}`} />
                      </div>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => !isCurrentTier && handleSubscribe(tier.tier_key)}
                  disabled={isCurrentTier || subscribing === tier.tier_key}
                  className={`w-full py-3 px-6 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                    isCurrentTier
                      ? 'bg-green-500/20 text-green-400 cursor-default'
                      : `${colors.button} text-white`
                  }`}
                >
                  {subscribing === tier.tier_key ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isCurrentTier ? (
                    <>
                      <Check className="w-5 h-5" />
                      Current Plan
                    </>
                  ) : tier.price_monthly === 0 ? (
                    <>
                      Get Started
                      <ArrowRight className="w-5 h-5" />
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Upgrade Now
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Feature Comparison Table */}
      {comparison && (
        <div className="max-w-6xl mx-auto px-4 pb-16">
          <h2 className="text-2xl font-bold text-center mb-8">Compare All Features</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Feature</th>
                  {comparison.tiers.map(tier => (
                    <th key={tier} className="text-center py-4 px-6 capitalize font-semibold">
                      {tier}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparison.features.map((feature, idx) => (
                  <tr key={idx} className="border-b border-gray-800">
                    <td className="py-4 px-6 text-gray-300">{feature.name}</td>
                    {comparison.tiers.map(tier => (
                      <td key={tier} className="text-center py-4 px-6">
                        {typeof feature[tier] === 'boolean' ? (
                          feature[tier] ? (
                            <Check className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-gray-600 mx-auto" />
                          )
                        ) : (
                          <span className={feature[tier] === 'Unlimited' ? 'text-amber-400 font-semibold' : 'text-gray-300'}>
                            {feature[tier]}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
        
        <div className="space-y-4">
          {[
            {
              q: "What are Skill Trees?",
              a: "Skill Trees are progression paths that help you level up in specific areas like writing, art, peer reviewing, and gaming. Each tree contains skills you unlock by completing activities and earning XP."
            },
            {
              q: "Can I change my subscription later?",
              a: "Yes! You can upgrade, downgrade, or cancel your subscription at any time. When upgrading, you'll get immediate access to new features. When downgrading, changes take effect at the end of your billing period."
            },
            {
              q: "What happens to my progress if I downgrade?",
              a: "Your progress and completed skills are always saved. However, if you exceed the skill tree limit for your new tier, you won't be able to unlock new trees until you upgrade again or reach completion on existing ones."
            },
            {
              q: "Do you offer refunds?",
              a: "We offer a 7-day money-back guarantee for new subscriptions. If you're not satisfied, contact support within 7 days of your first payment."
            },
            {
              q: "Is there a student discount?",
              a: "Yes! Students with a valid .edu email can get 50% off Premium and Pro subscriptions. Contact support with proof of enrollment."
            }
          ].map((faq, idx) => (
            <details key={idx} className="group bg-gray-800/50 rounded-xl">
              <summary className="flex items-center justify-between cursor-pointer p-6">
                <span className="font-semibold">{faq.q}</span>
                <span className="text-gray-400 group-open:rotate-180 transition-transform">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </summary>
              <div className="px-6 pb-6 text-gray-400">
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 py-16 px-4 text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Level Up?</h2>
        <p className="text-gray-400 max-w-2xl mx-auto mb-8">
          Join thousands of creators who are developing their skills, earning achievements, and building their creative portfolios.
        </p>
        <button
          onClick={() => handleSubscribe('premium')}
          className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-semibold px-8 py-4 rounded-xl hover:opacity-90 transition-opacity inline-flex items-center gap-2"
        >
          <Crown className="w-5 h-5" />
          Start Your Journey
        </button>
      </div>

      {/* Stripe Checkout Modal */}
      <StripeCheckoutModal
        isOpen={checkoutModal.isOpen}
        onClose={handleCheckoutClose}
        clientSecret={checkoutModal.clientSecret}
        tierName={checkoutModal.tierName}
        amount={checkoutModal.amount}
        billingPeriod={billingPeriod}
        onSuccess={handleCheckoutSuccess}
      />
    </div>
  );
};

export default Pricing;
